import {
    getBalance,
    getBlock,
    getTransactionCount,
    getTransactionReceipt,
    readContract,
    waitForTransactionReceipt,
    writeContract,
} from "@wagmi/core";
import { wagmiConfig } from "../config/wagmiConfig";
import { formatEther, parseEther, parseGwei } from "viem";
import { getGasTiers } from "./helper";

// ============================================== CONTRACT READ CALLS ==============================================

export const fetchTokenBalance = async (walletAddress, tokenAddress) => {
    try {
        let args;
        if (tokenAddress) {
            args = {
                address: walletAddress,
                token: tokenAddress,
            };
        } else {
            args = {
                address: walletAddress,
            };
        }

        const balance = await getBalance(wagmiConfig, args);
        return balance?.formatted;
    } catch (error) {
        throw new Error(error);
    }
};

export const estimateGasFee = async (
    obj,
    publicClient,
    sender,
    callingFunction
) => {
    let { stakeContract, tokenContractAbi, amount } = obj;
    const amountInString = amount?.toString()
    try {
        return await publicClient.estimateContractGas({
            address: stakeContract,
            abi: tokenContractAbi,
            functionName: callingFunction,
            value: parseEther(amountInString),
            gas: 12000000,
            account: sender,
            gasPrice: parseGwei("1"),
        });
    } catch (error) {
        console.log(error)
        throw error;
    }
};

export const getWaitTransaction = async (hash) => {
    try {
        return await waitForTransactionReceipt(wagmiConfig, {
            hash,
        });
    } catch (error) {
        throw new Error(error);
    }
};

export const fetchTrxTime = (hexString) => {
    // Remove '0x' prefix if present
    hexString = hexString.startsWith("0x") ? hexString.slice(2) : hexString;

    let timestampHex;

    // Check if hex string is long enough for timestamp extraction
    if (hexString.length >= 64) {
        // Extract the last 8 bytes (16 hex characters) for the timestamp
        timestampHex = hexString.slice(-16); // Get the last 16 characters (8 bytes)
    } else {
        throw new Error("Unsupported hex string format");
    }

    // Convert the hex timestamp to an integer
    const timestamp = parseInt(timestampHex, 16);
    // Return the Unix timestamp
    return timestamp;
};

export const getFailedTrxTime = async (hash) => {
    const trx = await getTransactionReceipt(wagmiConfig, { hash: hash });
    const blockTime = await getBlock(wagmiConfig, { block: trx.blockNumber });
    return Number(blockTime.timestamp);
};

export const fetchTotalStakedToken = async (obj) => {
    const { stakeContract, tokenContractAbi, walletAddress } = obj;
    try {
        const totalStaked = await readContract(wagmiConfig, {
            address: stakeContract,
            abi: tokenContractAbi,
            functionName: "getUserStakes",
            args: [walletAddress],
        });

        return formatEther(totalStaked);
    } catch (error) {
        console.log(error);
        throw new Error(error);
    }
};


export const checkBalanceForAnyTransaction = async (availableBalance) => {
    try {
        const gasTier = await getGasTiers();
        return availableBalance >= gasTier?.data[0]?.tx_fee ? true : false;
    } catch (error) {
        throw error;
    }
};

export const getTrxCount = async (walletAddress) => {
    try {
        const transactionCount = await getTransactionCount(wagmiConfig, {
            address: walletAddress,
            blockTag: "pending",
        });
        return transactionCount;
    } catch (error) { }
};

export const getReward = async (obj) => {
    try {
        const { stakeContract, tokenContractAbi, walletAddress } = obj;
        const data = await readContract(wagmiConfig, {
            address: stakeContract,
            abi: tokenContractAbi,
            functionName: "getDelegatorsRewards",
            args: [walletAddress],
        });
        return formatEther(data);
    } catch (error) {

    }
}

// ============================================== CONTRACT WRITE CALLS ==============================================

export const executeContractWrite = async (obj) => {
    let {
        stakeContract,
        tokenContractAbi,
        userWalletAddress,
        amount,
        action,
        publicClient,
    } = obj;
    let contractConfig;
    const amountInString = amount?.toString();


    switch (action) {
        case "stake": {
            const gasFee = await estimateGasFee(obj, publicClient, userWalletAddress, "stake")
            contractConfig = {
                address: stakeContract,
                abi: tokenContractAbi,
                functionName: action,
                value: parseEther(amountInString),
                gas: gasFee,
            };
            break
        }
        case "requestUnstake":
            {
                contractConfig = {
                    address: stakeContract,
                    abi: tokenContractAbi,
                    functionName: action,
                    args: [
                        parseEther(amountInString),
                    ],
                };
                break
            }
        case "unstake":
            {
                contractConfig = {
                    address: stakeContract,
                    abi: tokenContractAbi,
                    functionName: action,
                };
                break
            }
        case "claimReward":
            {
                contractConfig = {
                    address: stakeContract,
                    abi: tokenContractAbi,
                    functionName: action,
                };
                break
            }

        default:
            break;
    }
    try {
        const hash = await writeContract(wagmiConfig, contractConfig);
        return hash;
    } catch (error) {
        console.log(error);
        throw error;
    }
};
